import { Fragment, useCallback, type FunctionComponent, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useHistory } from 'react-router'
import { Link } from 'react-router-dom'
import { ArrowLeftIcon } from '@instacart/ids-core'
import { TextTitleRow, useTheme } from '@retailer-platform/shared-components/src/tds'
import { useAbsoluteRouteMatch } from '../../../utils/routing/useAbsoluteRouteMatch.hooks'
import { getSettingsBackHref } from './settingsBackHrefStore'
import { useIsNavItemActive, useResolveNavHref } from './TdsNavigationBar'
import { type NavSettingsV3Item, useNavHierarchySettingsV3 } from './useNavHierarchySettingsV3'

const SIDEBAR_WIDTH = 273

// Section label above each group of rows. Mirrors the visual treatment of v2's
// L2Group label (small, uppercase-ish, low-contrast grey) but stays inline so
// the settings rail has zero coupling to v2 nav components.
const SettingsSectionHeader: FunctionComponent<{ title: string }> = ({ title }) => {
  const theme = useTheme()
  return (
    <div
      css={{
        padding: '4px 16px',
        fontSize: 12,
        fontWeight: 400,
        lineHeight: '20px',
        color: theme.colors.systemGrayscale60,
        cursor: 'default',
      }}
    >
      {title}
    </div>
  )
}

// One row in the settings sidebar. Self-resolves its href and active state via
// the same hooks the primary v3 nav uses, so highlighting stays consistent.
// Renders as a react-router <Link> for SPA-internal routes (preserves modifier-
// key behavior), as a plain <a target=_blank> for external hrefs, and as an
// inert <div> for placeholder items that don't have a destination yet.
const SettingsNavRow: FunctionComponent<{ item: NavSettingsV3Item }> = ({ item }) => {
  const theme = useTheme()
  const resolved = useResolveNavHref(item)
  const isActive = useIsNavItemActive(item)

  const baseStyles = {
    display: 'flex',
    alignItems: 'center',
    minHeight: 40,
    padding: '8px 16px 8px 13px',
    borderLeft: `3px solid ${isActive ? theme.colors.systemGrayscale80 : 'transparent'}`,
    backgroundColor: isActive ? theme.colors.systemGrayscale10 : 'transparent',
    color: theme.colors.systemGrayscale80,
    fontSize: 14,
    fontWeight: isActive ? 600 : 400,
    lineHeight: '20px',
    textDecoration: 'none',
  } as const

  const interactiveStyles = {
    ...baseStyles,
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.colors.systemGrayscale10 },
  } as const

  const label: ReactNode = <FormattedMessage id={item.name} />

  if (!resolved?.href) {
    return (
      <div
        css={{ ...baseStyles, color: theme.colors.systemGrayscale30, cursor: 'default' }}
        aria-disabled="true"
      >
        {label}
      </div>
    )
  }

  if (resolved.isExternal) {
    return (
      <a href={resolved.href} target="_blank" rel="noreferrer" css={interactiveStyles}>
        {label}
      </a>
    )
  }

  return (
    <Link to={resolved.href} css={interactiveStyles}>
      {label}
    </Link>
  )
}

type Props = {
  // Route name to fall back to when the user clicks Back without an entry-page
  // captured in the module-scoped store (e.g. cold-loaded directly into a
  // settings URL). Resolved via the same useResolveNavHref hook used by NavItems.
  homeRoute: string
}

export const TdsSettingsSidebar: FunctionComponent<Props> = ({ homeRoute }) => {
  const intl = useIntl()
  const theme = useTheme()
  const history = useHistory()
  const routeMatch = useAbsoluteRouteMatch()
  const { sections, indexedRoutes } = useNavHierarchySettingsV3()
  const homeHref = useResolveNavHref({ route: homeRoute })?.href

  const handleBackClick = useCallback(() => {
    const stored = getSettingsBackHref()
    const target = stored ?? homeHref
    if (target) history.push(target)
  }, [history, homeHref])

  // Hide on every route that isn't part of the settings hierarchy. This is the
  // single switch that makes the sidebar conditional — the component is mounted
  // unconditionally in the v3 shell and self-hides here.
  if (!routeMatch?.route || !indexedRoutes[routeMatch.route]) {
    return null
  }

  return (
    <aside
      aria-label={intl.formatMessage({ id: 'settings.title' })}
      css={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        height: '100%',
        overflowY: 'auto',
        borderRight: `1px solid ${theme.colors.systemGrayscale20}`,
        backgroundColor: theme.colors.systemGrayscale00,
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 0',
        gap: '8px',
      }}
    >
      <button
        type="button"
        onClick={handleBackClick}
        css={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '8px 16px',
          background: 'none',
          border: 'none',
          color: theme.colors.systemGrayscale60,
          cursor: 'pointer',
          font: 'inherit',
          textAlign: 'left',
          '&:hover': { color: theme.colors.systemGrayscale80 },
        }}
      >
        <ArrowLeftIcon size={16} fill="currentColor" />
        <FormattedMessage id="navV3.settings.back" />
      </button>
      <div css={{ padding: '0 16px' }}>
        <TextTitleRow title={intl.formatMessage({ id: 'settings.title' })} />
      </div>
      <nav aria-label={intl.formatMessage({ id: 'settings.title' })}>
        {sections.map((section, sIdx) => (
          <Fragment key={`${section.name}-${sIdx}`}>
            <SettingsSectionHeader title={intl.formatMessage({ id: section.name })} />
            {section.items.map((item, iIdx) => (
              <SettingsNavRow key={`${item.name}-${iIdx}`} item={item} />
            ))}
          </Fragment>
        ))}
      </nav>
    </aside>
  )
}
